import { Button, Center, Loader, Paper, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getExport } from "../../../services/exports/exportsEndpoints";
import { ExportResponse } from "../../../services/exports/exportsEndpoints.interface";
import notify from "../../../utils/notify";

const ExportDownloadHandler = () => {
  const { workspaceId, exportId } = useParams<{ workspaceId: string; exportId: string }>();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [exportData, setExportData] = useState<ExportResponse | null>(null);

  // Set a timeout to prevent infinite loading state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (status === "loading") {
        setStatus("error");
        setErrorMessage("Download timed out. Please try again.");
      }
    }, 20000); // 20 seconds timeout
    
    return () => clearTimeout(timeoutId);
  }, [status]);
  
  useEffect(() => {
    if (!workspaceId || !exportId) {
      setStatus("error");
      setErrorMessage("Invalid download link. Missing parameters");
      notify({
        messageList: ["Invalid download link. Missing parameters"],
        type: "failure",
      });
      return;
    }
    
    // First fetch the export details to get the download URL
    getExport({
      workspace_id: workspaceId,
      id: exportId,
    })
      .then((response) => {
        const exportDetails = response.data;
        setExportData(exportDetails);
        console.log("Export details:", exportDetails);
        
        // Check if the export is completed and has a download URL
        if (exportDetails.status.toLowerCase() === "completed" && exportDetails.download_url) {
          const url = exportDetails.download_url;
          console.log("Download URL from export:", url);
          
          setDownloadUrl(url);
          setStatus("success");
          
          // Automatically redirect after a short delay
          setTimeout(() => {
            window.location.href = url;
          }, 1000);
          
          notify({
            messageList: ["Download started"],
            type: "success",
          });
        } else if (exportDetails.status.toLowerCase() !== "completed") {
          // If the export is not completed yet
          setStatus("error");
          setErrorMessage(`Export is not ready yet. Current status: ${exportDetails.status}`);
          notify({
            messageList: [`Export is not ready yet. Current status: ${exportDetails.status}`],
            type: "warning",
          });
        } else {
          // If the export is completed but doesn't have a download URL
          setStatus("error");
          setErrorMessage("Export is ready but no download URL is available");
          notify({
            messageList: ["Export is ready but no download URL is available"],
            type: "failure",
          });
        }
      })
      .catch((error: any) => {
        console.error("Export fetch error:", error);
        setStatus("error");
        const errorMsg = error?.response?.data?.message || "Failed to fetch export details";
        setErrorMessage(errorMsg);
        notify({
          messageList: [errorMsg],
          type: "failure",
        });
      });
  }, [workspaceId, exportId]); // Run when params change

  const handleRetry = () => {
    // Reset state and try again
    setStatus("loading");
    setErrorMessage("");
    window.location.reload();
  };
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <Paper shadow="sm" p="xl" radius="md" className="max-w-md w-full" withBorder>
        <Center>
          <div className="flex flex-col items-center py-8">
            {status === "loading" && (
              <>
                <Loader size="lg" />
                <Text fw={500} size="lg" mt="md">Preparing your download...</Text>
                <Text c="dimmed" size="sm" mt={4} ta="center">
                  Please wait while we prepare your export file for download.
                </Text>
              </>
            )}
            
            {status === "success" && (
              <>
                <div className="text-green-500 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <Text fw={500} size="lg">Download started!</Text>
                <Text c="dimmed" size="sm" mt={4} ta="center">
                  Your download should begin automatically. If it doesn&apos;t start,
                  click the button below.
                </Text>
                <Text size="xs" fw={500} mt={2} c="dimmed">
                  {exportData?.view?.view_name || "Export"}
                </Text>
                <Button 
                  onClick={() => { window.location.href = downloadUrl; }}
                  mt="md"
                >
                  Download Again
                </Button>
              </>
            )}
            
            {status === "error" && (
              <>
                <div className="text-red-500 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <Text c="red" size="md" mt={4} ta="center">
                  {errorMessage}
                </Text>
                <div className="flex gap-3 mt-4">
                  <Button 
                    onClick={handleRetry}
                    variant="purple"
                  >
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </div>
        </Center>
      </Paper>
    </div>
  );
};

export default ExportDownloadHandler;
